import { useEffect, useRef, useState } from "react";
import { X, Download, Share2, Check } from "lucide-react";

// Generates a 1080x1350 (story-friendly 4:5) PNG of an outfit rating card on a canvas.
async function renderShareCard({ title, overall, breakdown, summary, photoBase64 }) {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background — solid black with subtle vignette
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, W, H);
  const grad = ctx.createRadialGradient(W * 0.2, H * 0.15, 80, W / 2, H / 2, H * 0.8);
  grad.addColorStop(0, "rgba(255,255,255,0.04)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Brand
  ctx.fillStyle = "#fff";
  ctx.font = "italic 400 44px Fraunces, serif";
  ctx.fillText("NOVA", 80, 130);

  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "500 22px Inter Tight, sans-serif";
  ctx.fillText("AI STYLIST", W - 80 - ctx.measureText("AI STYLIST").width, 130);

  // Photo (left half)
  const photoX = 80, photoY = 200, photoW = 460, photoH = 600;
  ctx.fillStyle = "#111";
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(photoX, photoY, photoW, photoH, 28);
  else ctx.rect(photoX, photoY, photoW, photoH);
  ctx.fill();

  if (photoBase64) {
    await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        ctx.save();
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(photoX, photoY, photoW, photoH, 28);
        else ctx.rect(photoX, photoY, photoW, photoH);
        ctx.clip();
        // cover fit
        const r = Math.max(photoW / img.width, photoH / img.height);
        const w = img.width * r, h = img.height * r;
        ctx.drawImage(img, photoX + (photoW - w) / 2, photoY + (photoH - h) / 2, w, h);
        ctx.restore();
        resolve();
      };
      img.onerror = resolve;
      img.src = photoBase64;
    });
  }

  // Score donut (right side, top)
  const cx = 800, cy = 340, r = 110;
  // track
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.lineWidth = 14;
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.stroke();
  // fill
  const score = Math.max(0, Math.min(10, Number(overall) || 0));
  ctx.beginPath();
  ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (score / 10) * Math.PI * 2);
  ctx.strokeStyle = "#fff";
  ctx.lineCap = "round";
  ctx.stroke();
  // big number
  ctx.fillStyle = "#fff";
  ctx.font = "400 78px Fraunces, serif";
  const s = Number(score).toFixed(1);
  const sw = ctx.measureText(s).width;
  ctx.fillText(s, cx - sw / 2, cy + 18);
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "500 18px Inter Tight, sans-serif";
  const subw = ctx.measureText("/ 10").width;
  ctx.fillText("/ 10", cx - subw / 2, cy + 50);

  // Title
  ctx.fillStyle = "#fff";
  ctx.font = "400 44px Fraunces, serif";
  wrapText(ctx, title || "Outfit", 600, 540, 420, 50);

  // breakdown bars
  const bars = breakdown || [];
  let by = 880;
  ctx.font = "500 22px Inter Tight, sans-serif";
  for (const b of bars.slice(0, 5)) {
    const label = String(b.label || "").toUpperCase();
    const val = Math.max(0, Math.min(10, Number(b.score) || 0));
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fillText(label, 80, by);
    ctx.fillStyle = "#fff";
    ctx.fillText(val.toFixed(1), W - 80 - ctx.measureText(val.toFixed(1)).width, by);
    // bar
    const barX = 80, barY = by + 14, barW = W - 160, barH = 6;
    ctx.fillStyle = "rgba(255,255,255,0.10)";
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(barX, barY, barW, barH, 999);
    else ctx.rect(barX, barY, barW, barH);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(barX, barY, barW * (val / 10), barH, 999);
    else ctx.rect(barX, barY, barW * (val / 10), barH);
    ctx.fill();
    by += 70;
  }

  // Footer
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "500 22px Inter Tight, sans-serif";
  ctx.fillText("Rate your outfit on Nova", 80, H - 70);

  return canvas.toDataURL("image/png", 0.95);
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(" ");
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y);
      line = w;
      y += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, y);
}

export default function ShareModal({ open, onClose, payload }) {
  // payload: { title, overall, breakdown:[{label,score}], summary, photoBase64 }
  const [dataUrl, setDataUrl] = useState(null);
  const [busy, setBusy] = useState(true);
  const [shared, setShared] = useState(false);
  const dlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !payload) return;
    setBusy(true); setDataUrl(null); setShared(false);
    renderShareCard(payload).then((url) => {
      if (!cancelled) {
        setDataUrl(url);
        setBusy(false);
      }
    });
    return () => { cancelled = true; };
  }, [open, payload]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `stylerate-${Date.now()}.png`;
    a.click();
  };

  const share = async () => {
    if (!dataUrl) return;
    try {
      // Convert dataURL -> Blob -> File for Web Share API
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "stylerate.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Nova",
          text: `My outfit scored ${Number(payload?.overall || 0).toFixed(1)}/10 on Nova.`,
        });
        setShared(true);
      } else {
        // Fallback to download
        download();
        setShared(true);
      }
    } catch {
      // Probably user cancelled — silent
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      data-testid="share-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)",
        zIndex: 400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "fadeIn 280ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0a0a0a",
          border: "1px solid var(--line-strong)",
          borderRadius: 24,
          maxWidth: 400,
          width: "100%",
          padding: 18,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="h3">Share your rating</div>
          <button className="icon-btn" onClick={onClose} aria-label="Close" data-testid="share-close-btn">
            <X size={16} />
          </button>
        </div>

        <div style={{
          aspectRatio: "1080 / 1350",
          borderRadius: 16,
          overflow: "hidden",
          background: "#0c0c0c",
          border: "1px solid var(--line)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {busy && <div className="spinner spinner-lg" />}
          {!busy && dataUrl && (
            <img
              ref={dlRef}
              src={dataUrl}
              alt="Share preview"
              style={{ width: "100%", height: "100%", display: "block" }}
              data-testid="share-preview-img"
            />
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button className="btn btn-primary" onClick={share} disabled={busy} data-testid="share-button">
            {shared ? (<><Check size={16} /> Shared</>) : (<><Share2 size={16} /> Share</>)}
          </button>
          <button className="btn btn-outline" onClick={download} disabled={busy} data-testid="share-download-btn">
            <Download size={16} /> Download image
          </button>
        </div>
      </div>
    </div>
  );
}
